import React, { useEffect, useMemo, useState } from "react";
import { Modal } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { solid } from "@fortawesome/fontawesome-svg-core/import.macro";

import Constants from "../../constants";
import { getErrorMessage } from "../../common";
import courseBlocksService from "../../services/v2/courseBlocks.service";
import { BLOCK_KIND, BLOCK_KIND_LABELS, BlockBody, BlockDTO, BlockKindId, BlockTargetDTO } from "../../models/CourseBlockDTO";

export interface CourseBlocksPromptProps {
	campusId: number;
	course: { id: number; name: string; code?: string };
	allowedUpdate: boolean;
	onClose: () => void;
}

interface BlockDraft {
	name: string;
	kindId: BlockKindId;
	electiveGroup: string;
	sessions: string;
	targetLevelId: string;
}

const NUEVO = -1;

const draftVacio: BlockDraft = { name: "", kindId: BLOCK_KIND.REQUIRED, electiveGroup: "", sessions: "", targetLevelId: "" };

const pill: React.CSSProperties = {
	display: "inline-block", padding: "2px 8px", fontSize: 10, fontWeight: 800, letterSpacing: "0.06em", textTransform: "uppercase",
	color: "#6B5F58", background: "#FAF7F4", border: "1px solid #E9E1DA", borderRadius: 999, whiteSpace: "nowrap"
};

const pillObligatorio: React.CSSProperties = { ...pill, color: "#F45030", borderColor: "#F45030" };

/** Kind pill: Obligatorio in orange, the rest neutral. */
export const BlockKindPill: React.FC<{ kindId: number; label?: string }> = ({ kindId, label }) => (
	<span style={kindId === BLOCK_KIND.REQUIRED ? pillObligatorio : pill}>{label || BLOCK_KIND_LABELS[kindId] || "-"}</span>
);

/** "Hotelería · Módulo 2" */
export const seTomaEn = (block: BlockDTO): string =>
	[block.targetCourseName, block.targetLevelName].filter(Boolean).join(" · ") || "-";

/**
 * Bloques de un curso compuesto (Esteban, 2026-09-18): the modules where the classes are taken and how many
 * sessions each is worth. Edited in place, one block at a time; the list is what the ficha del alumno assigns.
 */
const CourseBlocksPrompt: React.FC<CourseBlocksPromptProps> = ({ campusId, course, allowedUpdate, onClose }) => {
	const [blocks, setBlocks] = useState<BlockDTO[] | null>(null);
	const [targets, setTargets] = useState<BlockTargetDTO[]>([]);
	const [editingId, setEditingId] = useState<number | null>(null);
	const [draft, setDraft] = useState<BlockDraft>(draftVacio);
	const [busy, setBusy] = useState<boolean>(false);
	const [error, setError] = useState<string | null>(null);
	const [formError, setFormError] = useState<string | null>(null);

	useEffect(() => {
		let cancelled = false;
		courseBlocksService.getBlocks(campusId, course.id)
			.then((response) => { if (!cancelled) { setBlocks(response.data.blocks || []); } })
			.catch((e) => { if (!cancelled) { setBlocks([]); setError(getErrorMessage(e)); } });
		if (allowedUpdate) {
			courseBlocksService.getTargets(campusId)
				.then((response) => { if (!cancelled) { setTargets(response.data || []); } })
				.catch((e) => { if (!cancelled) { setError(getErrorMessage(e)); } });
		}
		return () => { cancelled = true; };
	}, [campusId, course.id, allowedUpdate]);

	//Target picker grouped by course, in Spanish alphabetical order.
	const targetsPorCurso = useMemo(() => {
		const grupos: { [courseName: string]: BlockTargetDTO[] } = {};
		targets.forEach((target) => {
			const clave = target.courseName || "-";
			(grupos[clave] = grupos[clave] || []).push(target);
		});
		return Object.keys(grupos).sort((a, b) => a.localeCompare(b, "es")).map((courseName) => ({
			courseName,
			levels: grupos[courseName].sort((a, b) => (a.levelName || "").localeCompare(b.levelName || "", "es"))
		}));
	}, [targets]);

	const sesionesObligatorias = (blocks || [])
		.filter((block) => block.kindId === BLOCK_KIND.REQUIRED)
		.reduce((suma, block) => suma + Number(block.sessions || 0), 0);

	const startNew = () => {
		setFormError(null);
		setDraft({ ...draftVacio, kindId: BLOCK_KIND.REQUIRED });
		setEditingId(NUEVO);
	};

	const startEdit = (block: BlockDTO) => {
		setFormError(null);
		setDraft({
			name: block.name || "",
			kindId: block.kindId,
			electiveGroup: block.electiveGroup || "",
			sessions: String(block.sessions ?? ""),
			targetLevelId: block.targetLevelId == null ? "" : String(block.targetLevelId)
		});
		setEditingId(block.id);
	};

	const cancelForm = () => {
		setEditingId(null);
		setFormError(null);
	};

	const save = async () => {
		const sessions = Number(draft.sessions);
		const targetLevelId = Number(draft.targetLevelId);
		if (draft.name.trim() === "" || !(sessions > 0) || !(targetLevelId > 0)) {
			setFormError(Constants.Common.requiredFieldsHint);
			return;
		}
		if (draft.kindId === BLOCK_KIND.ELECTIVE && draft.electiveGroup.trim() === "") {
			setFormError(Constants.Common.requiredFieldsHint);
			return;
		}
		const body: BlockBody = {
			name: draft.name.trim(),
			kindId: draft.kindId,
			electiveGroup: draft.kindId === BLOCK_KIND.ELECTIVE ? draft.electiveGroup.trim() : null,
			sessions,
			targetLevelId
		};
		setBusy(true);
		setFormError(null);
		try {
			if (editingId === NUEVO) {
				const response = await courseBlocksService.createBlock(campusId, course.id, body);
				setBlocks((previos) => [...(previos || []), response.data]);
			} else if (editingId !== null) {
				const response = await courseBlocksService.updateBlock(campusId, course.id, editingId, body);
				setBlocks((previos) => (previos || []).map((block) => block.id === editingId ? response.data : block));
			}
			setEditingId(null);
		} catch (e) {
			setFormError(getErrorMessage(e));
		} finally {
			setBusy(false);
		}
	};

	const remove = async (block: BlockDTO) => {
		if (!window.confirm(Constants.Common.confirmDeleteBlock)) {
			return;
		}
		setBusy(true);
		setError(null);
		try {
			await courseBlocksService.deleteBlock(campusId, course.id, block.id);
			setBlocks((previos) => (previos || []).filter((item) => item.id !== block.id));
			if (editingId === block.id) {
				setEditingId(null);
			}
		} catch (e) {
			setError(getErrorMessage(e));
		} finally {
			setBusy(false);
		}
	};

	const campo: React.CSSProperties = { fontSize: 12, color: "#6B5F58", marginBottom: 2, position: "static", display: "block" };

	const form = (
		<div style={{ background: "#FAF7F4", border: "1px solid #E9E1DA", borderRadius: 6, padding: "10px 14px", marginTop: 10 }}>
			<div className="row" style={{ rowGap: 8 }}>
				<div className="col-md-4">
					<div style={campo}>{Constants.Common.name}</div>
					<input className="form-control form-control-sm" type="text" maxLength={120} value={draft.name} disabled={busy}
						onChange={(event) => setDraft({ ...draft, name: event.target.value })} />
				</div>
				<div className="col-md-4">
					<div style={campo}>{Constants.Common.blockTakenIn}</div>
					<select className="form-select form-select-sm" value={draft.targetLevelId} disabled={busy}
						onChange={(event) => setDraft({ ...draft, targetLevelId: event.target.value })}>
						<option value="">{Constants.Common.selectModule}</option>
						{targetsPorCurso.map((grupo) => (
							<optgroup key={"g_" + grupo.courseName} label={grupo.courseName}>
								{grupo.levels.map((target) => (
									<option key={"t_" + target.levelId} value={String(target.levelId)}>{target.levelName}</option>
								))}
							</optgroup>
						))}
					</select>
				</div>
				<div className="col-md-1">
					<div style={campo}>{Constants.Common.blockSessions}</div>
					<input className="form-control form-control-sm" type="number" min={1} step={1} value={draft.sessions} disabled={busy}
						onChange={(event) => setDraft({ ...draft, sessions: event.target.value })} />
				</div>
				<div className="col-md-3">
					<div style={campo}>{Constants.Common.blockKind}</div>
					<select className="form-select form-select-sm" value={String(draft.kindId)} disabled={busy}
						onChange={(event) => setDraft({ ...draft, kindId: Number(event.target.value) as BlockKindId })}>
						{Object.keys(BLOCK_KIND_LABELS).map((kindId) => (
							<option key={"k_" + kindId} value={kindId}>{BLOCK_KIND_LABELS[Number(kindId)]}</option>
						))}
					</select>
				</div>
				{draft.kindId === BLOCK_KIND.ELECTIVE ? (
					<div className="col-md-4">
						<div style={campo}>{Constants.Common.electiveGroup}</div>
						<input className="form-control form-control-sm" type="text" maxLength={60} value={draft.electiveGroup} disabled={busy}
							placeholder="A" onChange={(event) => setDraft({ ...draft, electiveGroup: event.target.value })} />
					</div>
				) : ""}
			</div>
			{formError ? <div style={{ fontSize: 13, color: "#A8730A", marginTop: 6 }}>{formError}</div> : ""}
			<div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 8 }}>
				<button type="button" className="btn btn-outline-secondary btn-sm rounded-pill" disabled={busy} onClick={cancelForm}>
					{Constants.Common.close}
				</button>
				<button type="button" className="btn btn-primary btn-sm rounded-pill" disabled={busy} onClick={save}>
					{Constants.Common.save}
				</button>
			</div>
		</div>
	);

	return (
		<Modal show onHide={busy ? undefined : onClose} size="lg" centered scrollable>
			<Modal.Header closeButton={!busy}>
				<Modal.Title style={{ fontSize: 18 }}>
					{Constants.Common.blocks} · <b>{course.name}</b>
				</Modal.Title>
			</Modal.Header>
			<Modal.Body style={{ textAlign: "left" }}>
				{error ? <div className="alert alert-danger" style={{ fontSize: 13 }}>{error}</div> : ""}
				{blocks === null ? <div className="loader" /> : (
					<>
						{blocks.length < 1 ? (
							<div style={{ padding: 15, color: "#6B5F58", fontSize: 13 }}>{Constants.Common.noBlocks}</div>
						) : (
							<table className="table table-sm" style={{ fontSize: 13, marginBottom: 0 }}>
								<thead>
									<tr>
										<th>{Constants.Common.blockName}</th>
										<th>{Constants.Common.blockTakenIn}</th>
										<th style={{ textAlign: "right" }}>{Constants.Common.blockSessions}</th>
										<th>{Constants.Common.blockKind}</th>
										<th>{Constants.Common.electiveGroup}</th>
										{allowedUpdate ? <th /> : null}
									</tr>
								</thead>
								<tbody>
									{blocks.map((block) => (
										<tr key={"b_" + block.id} style={{ background: editingId === block.id ? "#F6F1ED" : undefined }}>
											<td><b>{block.name}</b></td>
											<td>{seTomaEn(block)}</td>
											<td style={{ textAlign: "right", whiteSpace: "nowrap" }}>{block.sessions}</td>
											<td><BlockKindPill kindId={block.kindId} label={block.kindLabel} /></td>
											<td>{block.kindId === BLOCK_KIND.ELECTIVE ? (block.electiveGroup || "-") : ""}</td>
											{allowedUpdate ? (
												<td style={{ whiteSpace: "nowrap", textAlign: "right" }}>
													<button type="button" className="btn btn-link btn-sm" style={{ padding: "0 6px" }} title={Constants.Common.edit}
														disabled={busy} onClick={() => startEdit(block)}>
														<FontAwesomeIcon icon={solid("pen")} />
													</button>
													<button type="button" className="btn btn-link btn-sm" style={{ padding: "0 6px", color: "#A8730A" }} title={Constants.Common.delete}
														disabled={busy} onClick={() => remove(block)}>
														<FontAwesomeIcon icon={solid("trash")} />
													</button>
												</td>
											) : null}
										</tr>
									))}
								</tbody>
							</table>
						)}
						{allowedUpdate && editingId !== null ? form : ""}
						<div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10, marginTop: 12, fontSize: 13 }}>
							<span style={{ color: "#6B5F58" }}>
								{Constants.Common.requiredSessionsTotal.replace("{count}", String(sesionesObligatorias))}
							</span>
							{allowedUpdate && editingId === null ? (
								<button type="button" className="btn btn-primary btn-sm rounded-pill" disabled={busy} onClick={startNew}>
									<FontAwesomeIcon icon={solid("plus")} />&nbsp;{Constants.Common.addBlock}
								</button>
							) : ""}
						</div>
					</>
				)}
			</Modal.Body>
		</Modal>
	);
};

export default CourseBlocksPrompt;
